import { format, parseISO } from 'date-fns'
import { MeetingProcessingStatus, MeetingStatus, hasMeetingDetailsSettled } from '../../meetings.service'
import { ActionItem, ActionItemsOverview, ActionItemType } from './action-items.service'

export type ActionItemsView = 'BY_TYPE' | 'IN_MEETING_ORDER'

export type ActionItemsMeetingState = {
  status: MeetingStatus
  isPostProcessingComplete: boolean
  processingStatus: MeetingProcessingStatus | null
}

/** Whether detection is still to come: the meeting has ended and its processing, which ends with detection, is under way. */
export function isAwaitingActionItemDetection(overview: ActionItemsOverview, meeting: ActionItemsMeetingState): boolean {
  return overview.detectionStatus === 'NOT_STARTED' && meeting.status === MeetingStatus.COMPLETED && !hasMeetingDetailsSettled(meeting)
}

/** Whether the meeting has anything for the Actions tab: action items, or a detection run that is to come, running or has run. */
export function hasActionItemsTab(overview: ActionItemsOverview, meeting: ActionItemsMeetingState): boolean {
  return overview.items.length > 0 || overview.detectionStatus !== 'NOT_STARTED' || isAwaitingActionItemDetection(overview, meeting)
}

export type ActionItemGroupKey = ActionItemType | 'NOT_AN_ACTION'

export type ActionItemGroup = {
  key: ActionItemGroupKey
  items: ActionItem[]
}

export const ACTION_ITEM_TYPES: ActionItemType[] = ['AGREED', 'COMMITTED', 'PROPOSED']

/** One group per type, then the items marked as not an action, a group that only appears once there are any. */
export function groupByType(items: ActionItem[]): ActionItemGroup[] {
  const typeGroups = ACTION_ITEM_TYPES.map((type) => ({ key: type, items: items.filter((item) => !item.isMarkedAsNotAnAction && item.type === type) }))
  const notAnAction = items.filter((item) => item.isMarkedAsNotAnAction)
  return notAnAction.length === 0 ? typeGroups : [...typeGroups, { key: 'NOT_AN_ACTION', items: notAnAction }]
}

export function firstMentionSeconds(item: ActionItem): number | null {
  return item.passages.length === 0 ? null : Math.min(...item.passages.map((passage) => passage.startTimeSeconds))
}

/** In the order the meeting first discussed them; items with nothing to place them by, like added ones, come first. */
export function inMeetingOrder(items: ActionItem[]): ActionItem[] {
  const unplaced = items.filter((item) => firstMentionSeconds(item) === null)
  const placed = items.filter((item) => firstMentionSeconds(item) !== null).sort((a, b) => (firstMentionSeconds(a) ?? 0) - (firstMentionSeconds(b) ?? 0))
  return [...unplaced, ...placed]
}

export type DueLabel = {
  /** What the due cell shows; null when there is no due date at all. */
  main: string | null
  /** What was said in the meeting, shown under a date someone set. */
  fromTheMeeting: string | null
}

export function dueLabel(item: Pick<ActionItem, 'dueDate' | 'dueText'>): DueLabel {
  if (item.dueDate !== null) {
    return { main: format(parseISO(item.dueDate), 'd MMM yyyy'), fromTheMeeting: item.dueText }
  }
  return { main: item.dueText, fromTheMeeting: null }
}

export function toIsoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}
